import { format } from 'date-fns';
import { FileReference, FloorplanRevision, VehicleType } from '@thive/fddm-types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { PROJECT_FILES_ROOT_CONTAINER } from '@/modules/api/constant';
import { downloadFile } from '@/modules/common/helpers/browser';
import {
  buildRevisionFileStorageBasePath,
  formatRevisionFileStorageBasePath,
} from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { logger } from '@common/logging/logger';
import { useZipFileProcessor } from '@/modules/floorplanV2/hooks/useZipFileProcessor';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { referenceImageFileNameUtils } from '@/modules/referenceImage/utils';

import { useFloorplanAdminStore } from '../store';
import { SimulationUtil } from '@/modules/simulation/SimulationUtil';

type VehicleFiles = {
  kmMdbFile?: Blob;
  uvtFile: object;
  vehicleTypeId: string;
};

type SimulationFile = {
  file: object | Blob;
  type: FileReference['type'];
  name: string;
};

const buildRevisionFile = (
  revisionSource: FloorplanRevision,
  layoutAlternativeName: string,
): File => {
  const revision = structuredClone(revisionSource);
  revision.uxonly.thumbnail = '';

  const fileName = `Floorplan_${layoutAlternativeName}-Version_${
    revision.metaData.version
  }-${format(new Date(), 'yyyyMMdd')}.json`;

  return new File([JSON.stringify(revision)], fileName, {
    type: 'application/json',
  });
};

export const useFloorplanDownload = () => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const { createAssetZip } = useZipFileProcessor();
  const { showSnackbar } = useSnackbarStore();
  const fileApi = useMemo(() => FileServiceConnector.getInstance(), []);
  const [isDownloading, setIsDownloading] = useState(false);

  const getUvtFile = useCallback(
    async (vehicleType: VehicleType, storageBasePath: string): Promise<object> => {
      if (vehicleType.uvtFileRefOriginal) {
        return fileApi.getJsonFileByUrl(vehicleType.uvtFileRef);
      }

      const file = await fileApi.getBlobFileByCategory(
        `${storageBasePath}/${vehicleType.uvtFileRef}`,
        PROJECT_FILES_ROOT_CONTAINER,
      );

      return JSON.parse(await file.text()) as object;
    },
    [fileApi],
  );

  const getKmMdbFile = useCallback(
    async (vehicleType: VehicleType, storageBasePath: string): Promise<Blob | undefined> => {
      if (!vehicleType.kmMdbFileRef) return;

      if (vehicleType.kmMdbFileRefOriginal) {
        return fileApi.getBlobFileByUrl(vehicleType.kmMdbFileRef);
      }

      return fileApi.getBlobFileByCategory(
        `${storageBasePath}/${vehicleType.kmMdbFileRef}`,
        PROJECT_FILES_ROOT_CONTAINER,
      );
    },
    [fileApi],
  );

  const getVehicleFiles = useCallback(
    async (revision: FloorplanRevision): Promise<VehicleFiles[]> => {
      const storageBasePath = buildRevisionFileStorageBasePath(revision.metaData);
      if (!storageBasePath) {
        throw new Error('Could not resolve the floorplan revision storage path.');
      }

      return Promise.all(
        revision.setup.vehicleTypes.map(async (vehicleType) => ({
          vehicleTypeId: vehicleType.id,
          uvtFile: await getUvtFile(vehicleType, storageBasePath),
          kmMdbFile: await getKmMdbFile(vehicleType, storageBasePath),
        })),
      );
    },
    [getKmMdbFile, getUvtFile],
  );

  const getOrderProfileFile = useCallback(
    async (url: string, storageBasePath: string): Promise<Blob> =>
      fileApi.getBlobFileByCategory(`${storageBasePath}/${url}`, PROJECT_FILES_ROOT_CONTAINER),
    [fileApi],
  );

  const getFmsConfigFile = useCallback(
    async (url: string, storageBasePath: string): Promise<object> => {
      const file = await fileApi.getBlobFileByCategory(
        `${storageBasePath}/${url}`,
        PROJECT_FILES_ROOT_CONTAINER,
      );

      return JSON.parse(await file.text()) as object;
    },
    [fileApi],
  );

  const getSimulationFiles = useCallback(
    async (revision: FloorplanRevision): Promise<SimulationFile[]> => {
      const storageBasePath = buildRevisionFileStorageBasePath(revision.metaData);
      if (!storageBasePath) {
        throw new Error('Could not resolve the floorplan revision storage path.');
      }

      return Promise.all(
        revision.uxonly.catalog.fileReferences.map(async (fr) => {
          if (fr.type === 'CUSTOM_ORDER_PROFILE') {
            return {
              file: await getOrderProfileFile(fr.url, storageBasePath),
              type: fr.type,
              name: SimulationUtil.getFileNameFromUrl(fr.url),
            };
          }

          return {
            file: await getFmsConfigFile(fr.url, storageBasePath),
            type: fr.type,
            name: SimulationUtil.getFileNameFromUrl(fr.url),
          };
        }),
      );
    },
    [getFmsConfigFile, getOrderProfileFile],
  );

  const downloadSelectedFloorplan = useCallback(async () => {
    const { floorPlanGroups, selectedFloorplanIdsState } = useFloorplanAdminStore.getState();
    if (!projectId || selectedFloorplanIdsState.length !== 1) return;

    const selectedFloorplan = floorPlanGroups.find(
      (floorplan) => floorplan.alternativeId === selectedFloorplanIdsState[0],
    );
    if (!selectedFloorplan) {
      logger.error('Could not find the selected floorplan.');
      return;
    }

    setIsDownloading(true);

    try {
      const revision = await ProjectServiceConnector.getInstance().getFloorplanRevision({
        projectId,
        alternativeId: selectedFloorplan.alternativeId,
        revisionId: selectedFloorplan.lastRevisionId,
      });

      if (revision.setup.vehicleTypes.length === 0) {
        throw new Error('No vehicle types found in the selected floorplan.');
      }

      // Vehicle files
      const vehicleFiles = await getVehicleFiles(revision);
      const uvtFiles = vehicleFiles.map((vehicleFile) => vehicleFile.uvtFile);
      const uvtFileNames = vehicleFiles.map((vehicleFile) => vehicleFile.vehicleTypeId);
      const kmMdbVehicleFiles = vehicleFiles.filter(
        (vehicleFile): vehicleFile is VehicleFiles & { kmMdbFile: Blob } =>
          Boolean(vehicleFile.kmMdbFile),
      );
      const kmMdbFiles = kmMdbVehicleFiles.map((vehicleFile) => vehicleFile.kmMdbFile);
      const kmMdbFileNames = kmMdbVehicleFiles.map((vehicleFile) => vehicleFile.vehicleTypeId);
      const floorplanFile = buildRevisionFile(revision, selectedFloorplan.name);

      // Simulation files
      const simulationFiles = await getSimulationFiles(revision);
      const fmsConfigFiles = simulationFiles.filter((file) => file.type === 'CUSTOM_FMS_CONFIG');
      const fmsConfigFileNames = fmsConfigFiles.map((file) =>
        SimulationUtil.getFileNameFromUrl(file.name),
      );
      const fmsConfigObjects = fmsConfigFiles.map((file) => file.file);
      const orderProfileFiles = simulationFiles.filter(
        (file) => file.type === 'CUSTOM_ORDER_PROFILE',
      );
      const orderProfileFileNames = orderProfileFiles.map((file) =>
        SimulationUtil.getFileNameFromUrl(file.name),
      );
      const orderProfileBlobs = orderProfileFiles.map((file) => file.file as Blob);

      // Reference image
      const { referenceImage } = revision.uxonly;
      let referenceImageFile: Blob | null = null;
      if (referenceImage) {
        const { projectId, alternativeId, revisionId } = revision.metaData;
        const basePath = formatRevisionFileStorageBasePath(projectId, alternativeId, revisionId);
        referenceImageFile = await FileServiceConnector.getInstance().getBlobFileByUrl(
          `${PROJECT_FILES_ROOT_CONTAINER}?filename=${basePath}/${referenceImage.imageReference}`,
        );
      }

      const zipFile = await createAssetZip(
        uvtFiles,
        uvtFileNames,
        kmMdbFiles,
        kmMdbFileNames,
        fmsConfigObjects,
        fmsConfigFileNames,
        orderProfileBlobs,
        orderProfileFileNames,
        floorplanFile,
        referenceImage && referenceImageFile
          ? {
              blob: referenceImageFile,
              fileName: referenceImageFileNameUtils.getFilenameWithExtension(
                referenceImage.imageReference,
              ),
            }
          : undefined,
      );

      downloadFile(zipFile, '.zip', `${floorplanFile.name.slice(0, -5)}.zip`);
    } catch (error) {
      logger.error('Failed to download floorplan:', error);
      showSnackbar(t('floorplan_import_export.download_failed', 'Failed to download floorplan'));
    } finally {
      setIsDownloading(false);
    }
  }, [createAssetZip, getSimulationFiles, getVehicleFiles, projectId, showSnackbar, t]);

  return {
    downloadSelectedFloorplan,
    isDownloading,
  };
};
